import type { IScrollData } from '../../components/ControlSidebar/ScrollRank';
import type { GTooltipCardData } from '~/presentation/ui';

import { EScrollType, useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { useScrollMetricFormatters } from './useScrollMetricFormatters';
import { useHeatmapScrollTooltip } from '../scroll-config';
import { EHeatmapScrollTooltipKey } from '../../types';
import { InlineStack, Text } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { GDiv } from '~/presentation/ui';

export interface IScrollColumnConfig {
  label: string;
  labelAlignment?: 'start' | 'center' | 'end';
  tooltip?: GTooltipCardData;
  renderValue: (item: IScrollData) => React.ReactNode;
}

type TColumns = [IScrollColumnConfig, IScrollColumnConfig, IScrollColumnConfig];
type TUI = {
  columns: string;
};
interface IScrollColumnConfigs {
  data: IScrollColumnConfig[];
  ui: TUI;
}
export const useHeatmapScrollConfig = (): IScrollColumnConfigs => {
  const scrollType = useHeatmapSettingContext((s) => s.scrollType) ?? EScrollType.Depth;

  const { t } = useTranslation();
  const { formatMetric } = useScrollMetricFormatters();
  const { getTooltip } = useHeatmapScrollTooltip();

  const renderDepthCol1 = (item: IScrollData) => (
    <GDiv className="text-[--p-color-text-info-secondary]">
      <Text as="p" variant="bodySm" tone="inherit">
        {item.endY}%
      </Text>
    </GDiv>
  );

  const renderRangeCol1 = (item: IScrollData) => (
    <GDiv className="text-[--p-color-text-info-secondary]">
      <Text as="p" variant="bodySm" tone="inherit">
        {`${item.startY} - ${item.endY}%`}
      </Text>
    </GDiv>
  );

  const columns: Record<EScrollType, TColumns> = {
    [EScrollType.Depth]: [
      {
        label: t('% scrolled'),
        renderValue: renderDepthCol1,
        tooltip: getTooltip(EHeatmapScrollTooltipKey.PERC_SCROLLED_DEPTH),
      },
      {
        label: t('# of visitors'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.TOTAL_VISITOR),
        labelAlignment: 'end',
        renderValue: (item) => (
          <Text as="p" alignment="end">
            <Text as="span" variant="bodySm">
              {formatMetric('visitors', item.visitors)}
            </Text>{' '}
            <Text as="span" variant="bodySm">
              ({formatMetric('percUsers', item.percUsers)})
            </Text>
          </Text>
        ),
      },
      {
        label: t('% drop off'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.PERC_DROP_OFF),
        labelAlignment: 'end',
        renderValue: (item) => (
          <Text as="p" variant="bodySm" alignment="end">
            {formatMetric('percDropOff', item.percDropOff)}
          </Text>
        ),
      },
    ],

    [EScrollType.Attention]: [
      {
        label: t('% scrolled'),
        renderValue: renderRangeCol1,
        tooltip: getTooltip(EHeatmapScrollTooltipKey.PERC_SCROLLED),
      },
      {
        label: t('Avg time spent'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.AVG_TIME_SPENT),
        labelAlignment: 'end',
        renderValue: (item) => (
          <Text as="p" variant="bodySm" alignment="end">
            {formatMetric('avgDwellTimeMs', item.avgDwellTimeMs)}
          </Text>
        ),
      },
      {
        label: t('% of session length'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.PERC_OF_SESSION_LENGTH),
        labelAlignment: 'end',
        renderValue: (item) => (
          <Text as="p" variant="bodySm" alignment="end">
            {formatMetric('percDwellTimeMs', item.percDwellTimeMs)}
          </Text>
        ),
      },
    ],

    [EScrollType.Revenue]: [
      {
        label: t('% scrolled'),
        renderValue: renderRangeCol1,
        tooltip: getTooltip(EHeatmapScrollTooltipKey.PERC_SCROLLED),
      },
      {
        label: t('# of visitors converted'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.TOTAL_VISITORS_CONVERTED),
        labelAlignment: 'end',
        renderValue: (item) => (
          <InlineStack gap="100" align="end">
            <Text as="p" variant="bodySm" alignment="end">
              {formatMetric('convertedVisitors', item.convertedVisitors)}
            </Text>
            <Text as="span" variant="bodySm">
              ({formatMetric('percVisitorsConverted', item.percVisitorsConverted)})
            </Text>
          </InlineStack>
        ),
      },
      {
        label: t('Revenue'),
        tooltip: getTooltip(EHeatmapScrollTooltipKey.TOTAL_REVENUE),
        labelAlignment: 'end',
        renderValue: (item) => (
          <Text as="p" variant="bodySm" alignment="end">
            {formatMetric('revenue', item.revenue)}
          </Text>
        ),
      },
    ],
  };

  const ui: Record<EScrollType, TUI> = {
    [EScrollType.Depth]: {
      columns: '1fr 85px 1fr',
    },
    [EScrollType.Attention]: {
      columns: '1fr 85px 1fr',
    },
    [EScrollType.Revenue]: {
      columns: '1fr 85px 1fr',
    },
  };

  return { data: columns[scrollType], ui: ui[scrollType] };
};
